/* STENCIL IMPORTS */
import { Host, h } from "@stencil/core";
const ARROW_DOWN = "ArrowDown";
const ARROW_UP = "ArrowUp";
/**
 * @deprecated Use the `ch-combo-box-render` with `suggest = true`
 */
export class ChSuggest {
    constructor() {
        this.windowHidden = true;
        this.debounce = 300;
        this.label = undefined;
        this.labelPosition = "start";
        this.showLabel = true;
        this.value = undefined;
        this.showHeader = false;
        this.suggestTitle = undefined;
        this.cursorEnd = false;
    }
    /*
  INDEX:
  1.OWN PROPERTIES
  2.REFERENCE TO ELEMENTS
  3.STATE() VARIABLES
  4.PUBLIC PROPERTY API / WATCH'S
  5.EVENTS (EMIT)
  6.COMPONENT LIFECYCLE EVENTS
  7.LISTENERS
  8.PUBLIC METHODS API
  9.LOCAL METHODS
  10.RENDER() FUNCTION
  */
    // 1.OWN PROPERTIES //
    timeoutReference;
    keyEventsDictionary = {
        ArrowDown: (e) => {
            const newFocusedItem = this.getNewFocusedItem(e.currentFocusedItem, ARROW_DOWN);
            const nextFocusedItem = this.getNewFocusedItem(newFocusedItem, ARROW_DOWN);
            newFocusedItem && newFocusedItem.focus();
            if (!nextFocusedItem) {
                /* This is the last item. Adjust window scroll to be at the very bottom*/
                this.scrollListToBottom();
            }
        },
        ArrowUp: (e) => {
            const newFocusedItem = this.getNewFocusedItem(e.currentFocusedItem, ARROW_UP);
            const nextFocusedItem = this.getNewFocusedItem(newFocusedItem, ARROW_UP);
            newFocusedItem && newFocusedItem.focus();
            if (!nextFocusedItem) {
                /* This is the first item. Adjust window scroll to be at the very top*/
                this.scrollListToTop();
            }
        }
    };
    // 2. REFERENCE TO ELEMENTS //
    textInput;
    chWindow;
    slot;
    el;
    // 5.EVENTS (EMIT) //
    /**
     * This event is emitted every time there input events fires, and it emits the actual input value.
     */
    valueChanged;
    /**
     * This event is emitted when an item was selected.
     */
    selectionChanged;
    // 6.COMPONENT LIFECYCLE EVENTS //
    // 7.LISTENERS //
    itemSelectedHandler(event) {
        event.stopPropagation();
        this.value = event.detail.value;
        this.closeWindow();
        this.selectionChanged.emit(event.detail);
    }
    focusChangeAttemptHandler(event) {
        const keyEventHandler = this.keyEventsDictionary[event.detail.code];
        if (keyEventHandler) {
            const currentFocusedItem = event.detail.el;
            const chSuggestListItemsArray = this.getChSuggestListItems();
            const currentFocusedItemIndex = chSuggestListItemsArray.findIndex(item => {
                return item === currentFocusedItem;
            });
            const newItemToSetFocusOn = null;
            keyEventHandler({
                event: event.detail,
                currentFocusedItem: currentFocusedItem,
                chSuggestListItemsArray: chSuggestListItemsArray,
                currentFocusedItemIndex: currentFocusedItemIndex,
                newItemToSetFocusOn: newItemToSetFocusOn
            });
        }
    }
    windowClosedHandler() {
        this.textInput.focus();
        this.windowHidden = true;
        this.slot.innerHTML = "";
    }
    // 9.PUBLIC METHODS API //
    /**
     * @description It selects/highlights the input text.
     */
    async selectInputText() {
        this.textInput.focus();
        this.textInput.select();
    }
    // 10.LOCAL METHODS //
    evaluateSlotIsEmpty = () => {
        this.chWindow.hidden = !this.el.firstElementChild;
    };
    setFocusOnFirstItem = () => {
        const firstItem = this.el.querySelector("ch-suggest-list-item");
        firstItem && firstItem.focus();
    };
    getChSuggestListItems = () => Array.from(this.el.querySelectorAll("ch-suggest-list-item"));
    getNewFocusedItem = (currentFocusedItem, direction) => {
        /* Helper function that returns the list item that should get focus (the first one, or the last one)*/
        const getListChild = (list) => {
            const listItems = list.querySelectorAll("ch-suggest-list-item");
            let listChild = listItems && listItems[listItems.length - 1];
            if (direction === ARROW_DOWN) {
                listChild = list.querySelector("ch-suggest-list-item");
            }
            else {
                const parentListItems = list.querySelectorAll("ch-suggest-list-item");
                listChild =
                    parentListItems && parentListItems[parentListItems.length - 1];
            }
            return listChild;
        };
        if (!currentFocusedItem) {
            // @ts-expect-error: TODO: Fix this error
            return;
        }
        let newFocusedItem = direction === ARROW_DOWN
            ? currentFocusedItem.nextElementSibling
            : currentFocusedItem.previousElementSibling;
        if (newFocusedItem?.nodeName === "CH-SUGGEST-LIST") {
            newFocusedItem = getListChild(newFocusedItem);
        }
        else if (!newFocusedItem) {
            /* this could be the last item of a list, but not the last item*/
            const parent = currentFocusedItem.parentElement;
            const sibling = direction === ARROW_DOWN
                ? parent.nextElementSibling
                : parent.previousElementSibling;
            const parentIsList = parent.nodeName === "CH-SUGGEST-LIST";
            if (parentIsList &&
                sibling &&
                sibling.nodeName === "CH-SUGGEST-LIST-ITEM") {
                newFocusedItem =
                    direction === ARROW_DOWN
                        ? parent.nextElementSibling
                        : parent.previousElementSibling;
            }
            else if (parentIsList &&
                sibling &&
                sibling.nodeName === "CH-SUGGEST-LIST") {
                const parentList = direction === ARROW_DOWN
                    ? parent.nextElementSibling
                    : parent.previousElementSibling;
                let listChild;
                if (direction === ARROW_DOWN) {
                    listChild = parentList.querySelector("ch-suggest-list-item");
                }
                else {
                    listChild = getListChild(parentList);
                }
                newFocusedItem = listChild ? listChild : null;
            }
        }
        return newFocusedItem;
    };
    /**
     * Every time the input event is triggered, the value of the input is sent to processInputEvent, which is responsible for displaying a window with the suggested options. this.debounce is a delay that, along with clearTimeout, ensures that the window is only shown after the user has stopped typing.
     */
    handleInput = (e) => {
        const inputValue = e.target.value;
        this.value = inputValue;
        if (this.timeoutReference) {
            clearTimeout(this.timeoutReference);
        }
        this.timeoutReference = setTimeout(() => {
            this.processInputEvent(inputValue);
        }, this.debounce);
    };
    handleKeyDown = (e) => {
        if (e.key === ARROW_DOWN) {
            e.preventDefault();
            this.setFocusOnFirstItem();
        }
    };
    evaluateWindowMaxHeight = () => {
        const viewportHeight = window.innerHeight;
        const documentHeight = document.body.scrollHeight;
        const height = documentHeight >= viewportHeight ? documentHeight : viewportHeight;
        const inputBottomPosition = this.textInput.getBoundingClientRect().bottom + window.scrollY;
        const windowMaxHeight = height - inputBottomPosition + "px";
        this.el.style.setProperty("--window-max-height", windowMaxHeight);
    };
    scrollListToTop = () => {
        const partWindow = this.chWindow.shadowRoot.querySelector("[part='window']");
        partWindow.scrollTop = 0;
    };
    scrollListToBottom = () => {
        const partWindow = this.chWindow.shadowRoot.querySelector("[part='window']");
        partWindow.scrollTop = partWindow.scrollHeight;
    };
    processInputEvent = (inputValue) => {
        this.valueChanged.emit(inputValue);
        this.evaluateWindowMaxHeight();
    };
    closeWindow = () => {
        this.chWindow.hidden = true;
    };
    onFocusHandler = () => {
        if (this.cursorEnd) {
            this.textInput.setSelectionRange(this.textInput.value.length, this.textInput.value.length);
        }
    };
    // 10.RENDER() FUNCTION //
    render() {
        return (h(Host, { key: 'e369dd34cc97b653290f91c4e5829c66b5f0b435' }, h("div", { key: '19e24b607bacbb526db8faad746c636607c8d435', class: "main-wrapper", part: "main-wrapper" }, h("div", { key: 'b1d458805be511b1a5a22b914ff8e3aff3270c38', class: "label-input-wrapper", part: "label-input-wrapper" }, this.showLabel && this.label && (h("label", { key: 'd4eee023e99cf09bfff795907def63405527fb09', id: "label", htmlFor: "input", part: "label" }, this.label)), h("input", { key: '4f5f881064008435ad633f5a8c316160c3061f38', type: "text", id: "input", part: "input", class: "input", ref: el => (this.textInput = el), onInput: this.handleInput, onKeyDown: this.handleKeyDown, onFocus: this.onFocusHandler, value: this.value, autocomplete: "off", "aria-controls": "ch-window", "aria-label": !this.showLabel && this.label ? this.label : undefined, "aria-labelledby": this.showLabel && this.label ? "label" : undefined, "aria-expanded": this.windowHidden.toString() })), h("ch-window", { key: '571af118967925b3097641fc4372eddc2fbcf836', id: "ch-window", container: this.textInput, "close-on-outside-click": true, "close-on-escape": true, xAlign: "inside-start", yAlign: "outside-end", ref: el => (this.chWindow = el), showHeader: this.showHeader, caption: this.suggestTitle, exportparts: "\n            header:header, \n            caption:title, \n            close:close-button,\n            window:dropdown" }, h("slot", { key: '22768c116a4a9e7d72c3de0e513cabfac3ba9908', onSlotchange: this.evaluateSlotIsEmpty, ref: el => (this.slot = el) })))));
    }
    static get is() { return "ch-suggest"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["ch-suggest.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ch-suggest.css"]
        };
    }
    static get properties() {
        return {
            "debounce": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The debounce amount in milliseconds (This is the time the suggest waits after the user has finished typing, to show the suggestions)."
                },
                "attribute": "debounce",
                "reflect": false,
                "defaultValue": "300"
            },
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The label"
                },
                "attribute": "label",
                "reflect": false
            },
            "labelPosition": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "LabelPosition",
                    "resolved": "\"above\" | \"start\"",
                    "references": {
                        "LabelPosition": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::LabelPosition"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The label position"
                },
                "attribute": "label-position",
                "reflect": true,
                "defaultValue": "\"start\""
            },
            "showLabel": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Whether or not to display the label"
                },
                "attribute": "show-label",
                "reflect": false,
                "defaultValue": "true"
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This is the suggest value."
                },
                "attribute": "value",
                "reflect": false
            },
            "showHeader": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "false",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Wether or not the suggest has a header. The header will show the \"suggestTitle\" if provided, and a close button."
                },
                "attribute": "show-header",
                "reflect": false,
                "defaultValue": "false"
            },
            "suggestTitle": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The suggest title (optional). This is not the same as the \"label\", rather, this is the title that will appear inside the dropdown. This title will only be visible if \"showHeader\" is set to true."
                },
                "attribute": "suggest-title",
                "reflect": false
            },
            "cursorEnd": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "false",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If true, it will position the cursor at the end when the input is focused."
                },
                "attribute": "cursor-end",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "windowHidden": {}
        };
    }
    static get events() {
        return [{
                "method": "valueChanged",
                "name": "valueChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "This event is emitted every time there input events fires, and it emits the actual input value."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "selectionChanged",
                "name": "selectionChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "This event is emitted when an item was selected."
                },
                "complexType": {
                    "original": "SuggestItemSelectedEvent",
                    "resolved": "{ value: any; indexes: SuggestItemIndexes; description?: string; icon?: string; }",
                    "references": {
                        "SuggestItemSelectedEvent": {
                            "location": "import",
                            "path": "./suggest-list-item/ch-suggest-list-item",
                            "id": "src/deprecated-components/suggest/suggest-list-item/ch-suggest-list-item.tsx::SuggestItemSelectedEvent"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "selectInputText": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": [{
                            "name": "description",
                            "text": "It selects/highlights the input text."
                        }]
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get listeners() {
        return [{
                "name": "itemSelected",
                "method": "itemSelectedHandler",
                "target": undefined,
                "capture": true,
                "passive": false
            }, {
                "name": "focusChangeAttempt",
                "method": "focusChangeAttemptHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "windowClosed",
                "method": "windowClosedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
